import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Copy, LoaderCircle, Lock } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { formatDateTimeLocal } from '@/utils/timezone'
import { PHOTO_SHARES, REVOKE_PHOTO_SHARE } from '../api'
import { describePhotosError, photosErrorKey } from '../lib/errors'
import { shareStatusVariant } from '../lib/share'
import type { PhotoShare } from '../types'
import PhotoSharesSheetSkeleton from './PhotoSharesSheetSkeleton'

interface Props {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

const STATUS_KEYS = {
  ACTIVE: 'Photos_share_status_active',
  EXPIRED: 'Photos_share_status_expired',
  REVOKED: 'Photos_share_status_revoked',
}

const KIND_KEYS = {
  INTERNAL: 'Photos_share_kind_internal',
  EXTERNAL: 'Photos_share_kind_external',
}

const PhotoSharesSheet: React.FC<Props> = ({ projectId, open, onOpenChange }) => {
  const { t, i18n } = useTranslation()
  const [revoking, setRevoking] = useState<string[]>([])
  const { data, loading } = useQuery<{ photoShares: PhotoShare[] }>(PHOTO_SHARES, {
    variables: { projectId },
    skip: !open,
    fetchPolicy: 'cache-and-network',
  })
  const [revoke] = useMutation(REVOKE_PHOTO_SHARE, { context: { handlesErrors: true }, refetchQueries: ['photoShares'] })
  const shares = data?.photoShares ?? []

  const doRevoke = async (id: string) => {
    setRevoking((r) => r.concat(id))
    try {
      await revoke({ variables: { id } })
      toastr.success('', t('Photos_share_revoked_toast'))
    } catch (e: any) {
      toastr.error(t('Common_error'), describePhotosError(photosErrorKey(e), t))
    }
    setRevoking((r) => r.filter((x) => x !== id))
  }

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      toastr.success('', t('Photos_share_link_copied'))
    } catch (e) {
      toastr.error(t('Common_error'), t('Common_error'))
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>{t('Photos_shares')}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-2">
          {loading && !data ? (
            <PhotoSharesSheetSkeleton />
          ) : shares.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">{t('Photos_shares_empty')}</p>
          ) : (
            shares.map((s) => {
              const recipients = s.recipientUsers.map((u) => u.name).concat(s.recipientEmails).join(', ')
              const isRevoking = revoking.indexOf(s.id) >= 0
              return (
                <div key={s.id} className="space-y-1 rounded-md border p-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={s.kind === 'INTERNAL' ? 'info' : 'neutral'}>{t(KIND_KEYS[s.kind])}</Badge>
                    <Badge variant={shareStatusVariant(s.status)}>{t(STATUS_KEYS[s.status])}</Badge>
                    <span className="min-w-0 flex-1 truncate">{recipients}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 text-muted-foreground">
                    <span>{t('Photos_share_photo_count', { count: s.photoCount })}</span>
                    {s.expiresAt && <span>{t('Photos_share_expires')}: {formatDateTimeLocal(s.expiresAt, i18n.language, 'P')}</span>}
                    <span>{t('Photos_share_author')}: {s.createdBy.name}</span>
                  </div>
                  {s.kind === 'EXTERNAL' && (
                    <div className="flex flex-wrap items-center gap-x-3 text-muted-foreground">
                      <span>{t('Photos_share_views', { count: s.viewCount })} · {t('Photos_share_downloads', { count: s.downloadCount })}</span>
                      {s.lastAccessedAt && <span>{t('Photos_share_last_access', { date: formatDateTimeLocal(s.lastAccessedAt, i18n.language) })}</span>}
                      {s.hasPassword && (
                        <span className="inline-flex items-center gap-1">
                          <Lock className="h-3.5 w-3.5" />
                          {t('Photos_share_has_password')}
                        </span>
                      )}
                    </div>
                  )}
                  {(s.url || s.canRevoke) && (
                    <div className="flex items-center justify-end gap-2 pt-1">
                      {s.url && (
                        <Button variant="outline" size="sm" className="h-8" onClick={() => copy(s.url as string)}>
                          <Copy className="h-4 w-4" />
                          {t('Photos_share_copy_link')}
                        </Button>
                      )}
                      {s.canRevoke && (
                        <Button variant="outline" size="sm" className="h-8" disabled={isRevoking} onClick={() => doRevoke(s.id)}>
                          {isRevoking && <LoaderCircle className="h-4 w-4 animate-spin" />}
                          {t('Photos_share_revoke')}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default PhotoSharesSheet
